import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { TagIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { createColumnHelper } from '@tanstack/react-table';
import { truncate, upperFirst } from 'lodash-es';

import { useAppDispatch } from 'src/app/store/hooks';
import store from 'src/app/store/store';
import { VehicleRead } from 'src/common/external/bambi-api/bambiApi';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import { fleetSlice } from '../fleet.slice';
import { VehicleForm } from '../VehicleForm/VehicleForm';
import { isValidUSALicensePlate } from '../VehicleForm/VehicleFormStep1/isValidUSALicensePlate';
import { isValidVin } from '../VehicleForm/VehicleFormStep1/isValidVin';
import { VehicleTagColumnFilter } from './tags/VehicleTagColumnFilter';
import VehicleTagDisplay from './tags/VehicleTagDisplay';
const columnHelper = createColumnHelper<VehicleRead>();

function RowActions({ vehicle }: { vehicle: VehicleRead }) {
  const dispatch = useAppDispatch();
  const { isRestrictedSupport } = useUserRoles();

  return (
    <div className="flex items-center">
      {/* Should probably just remove this column but felt this was easier */}
      {!isRestrictedSupport && (
        <>
          <VehicleForm vehicle={vehicle} />
          <Button
            onClick={() => {
              dispatch(fleetSlice.actions.onAddVehicleTags(vehicle.id));
            }}
            variant="ghost"
            disabled={isRestrictedSupport}
            dataTestId="add-driver-tag-button"
            className="hover:text-mint"
          >
            <TagIcon className="h-5 w-5" />
          </Button>
        </>
      )}
    </div>
  );
}

export const columns = [
  columnHelper.accessor('nickname', {
    header: 'Name',
    cell: ({ getValue }) => {
      const truncateName = truncate(getValue(), {
        length: 30,
        omission: '...',
      });
      return <>{truncateName}</>;
    },
    enableSorting: true,
  }),
  columnHelper.accessor('can_be_scheduled', {
    header: 'Status',
    cell: ({ getValue }) => (
      <Badge
        label={getValue() ? 'Available' : 'Unavailable'}
        color={getValue() ? 'green' : 'red'}
      />
    ),
    enableSorting: true,
  }),
  columnHelper.display({
    id: 'vehicleMeta',
    header: 'Make / Model / Year / Color',
    cell: (props) => (
      <div>
        <strong>
          {truncate(`${props.row.original.year} ${props.row.original.make}`, {
            length: 30,
            omission: '...',
          })}{' '}
          {props.row.original.model}
        </strong>
        <div>{props.row.original.color}</div>
      </div>
    ),
  }),
  columnHelper.accessor('license_plate', {
    header: 'License Plate',
    cell: ({ row, getValue }) => {
      const licensePlate = getValue();
      const licensePlateState = row.original.license_plate_state;

      let isValidLicensePlate: string | boolean = true;
      if (licensePlate && licensePlateState) {
        isValidLicensePlate = isValidUSALicensePlate(
          licensePlate,
          licensePlateState
        );
      }

      return (
        <div className="flex items-center gap-2">
          {typeof isValidLicensePlate === 'string' && (
            <SimpleTooltip
              trigger={
                <div data-testid="invalid-license-plate-icon">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                </div>
              }
              content={
                'The license plate appears to be invalid. ' +
                'This may cause delays or rejections in trip payments from broker partners. ' +
                'Please double-check and correct the license plate information.'
              }
            />
          )}

          {licensePlate}
        </div>
      );
    },
  }),
  columnHelper.accessor('license_plate_state', {
    header: 'License State',
  }),
  columnHelper.accessor('vin', {
    header: 'VIN',
    cell: ({ getValue }) => {
      const vin = getValue();

      let isVinValid: string | boolean = true;
      if (vin) {
        isVinValid = isValidVin(vin);
      }

      return (
        <div className="flex items-center gap-2">
          {typeof isVinValid === 'string' && (
            <SimpleTooltip
              trigger={
                <div data-testid="invalid-vin-icon">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                </div>
              }
              content={
                'The VIN appears to be invalid. ' +
                'This may cause delays or rejections in trip payments from broker partners. ' +
                'Please double-check and correct the VIN information.'
              }
            />
          )}
          {vin}
        </div>
      );
    },
  }),
  columnHelper.accessor('category', {
    header: 'Type',
    cell: ({ getValue }) => (
      <Badge label={upperFirst(getValue())} color="blue" />
    ),
    enableSorting: true,
  }),
  columnHelper.accessor('tags', {
    header: 'Tags',
    cell: (props) => <VehicleTagDisplay tags={props.row.original.tags} />,
    meta: {
      filterComponent(column, open, onClose) {
        return <VehicleTagColumnFilter open={open} onClose={onClose} />;
      },
      filterCount() {
        const driverTags = store.getState().team.driverTags;
        return driverTags.length;
      },
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Manage',
    cell: (props) => <RowActions vehicle={props.row.original} />,
  }),
];
